import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material'
import { formatNumber } from '../../utils/format'

const MONTHS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar']

// -- Fallback demo data used when API returns empty/null --
const DEMO_PLANTS = {
  FBO: {
    name: 'Faribault (FBO)',
    data: [
      { cases: 143800, ecs: 107850, hours: 583 },
      { cases: 135900, ecs: 101925, hours: 551 },
      { cases: 152300, ecs: 114225, hours: 617 },
      { cases: 144800, ecs: 108600, hours: 590 },
      { cases: 135700, ecs: 101775, hours: 547 },
      { cases: 154300, ecs: 115725, hours: 630 },
      { cases: 141800, ecs: 106350, hours: 578 },
      { cases: 130500, ecs: 97875, hours: 527 },
      { cases: 159800, ecs: 119850, hours: 649 },
      { cases: 139200, ecs: 104400, hours: 567 },
      { cases: 137600, ecs: 103200, hours: 559 },
      { cases: 152300, ecs: 114225, hours: 615 },
    ],
  },
  TUC: {
    name: 'Tucson (TUC)',
    data: [
      { cases: 98200, ecs: 73650, hours: 412 },
      { cases: 92400, ecs: 69300, hours: 388 },
      { cases: 104600, ecs: 78450, hours: 439 },
      { cases: 99800, ecs: 74850, hours: 419 },
      { cases: 94200, ecs: 70650, hours: 395 },
      { cases: 106400, ecs: 79800, hours: 447 },
      { cases: 97600, ecs: 73200, hours: 410 },
      { cases: 88400, ecs: 66300, hours: 371 },
      { cases: 110200, ecs: 82650, hours: 463 },
      { cases: 95800, ecs: 71850, hours: 402 },
      { cases: 93400, ecs: 70050, hours: 392 },
      { cases: 105200, ecs: 78900, hours: 442 },
    ],
  },
}

export default function CrossPlantSummary({ data }) {
  const plants = data && Object.keys(data).length ? data : DEMO_PLANTS // fallback to demo

  const plantKeys = Object.keys(plants)

  const getCombined = (mi) => {
    let cases = 0, ecs = 0, hours = 0
    plantKeys.forEach((key) => {
      cases += plants[key].data[mi].cases
      ecs += plants[key].data[mi].ecs
      hours += plants[key].data[mi].hours
    })
    return { cases, ecs, hours }
  }

  const getPlantAnnual = (plantKey) => {
    const plantData = plants[plantKey].data
    return {
      cases: plantData.reduce((s, d) => s + d.cases, 0),
      ecs: plantData.reduce((s, d) => s + d.ecs, 0),
      hours: plantData.reduce((s, d) => s + d.hours, 0),
    }
  }

  const getCombinedAnnual = () => {
    let cases = 0, ecs = 0, hours = 0
    plantKeys.forEach((key) => {
      const a = getPlantAnnual(key)
      cases += a.cases
      ecs += a.ecs
      hours += a.hours
    })
    return { cases, ecs, hours }
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell
              rowSpan={2}
              sx={{
                fontWeight: 700,
                position: 'sticky',
                left: 0,
                backgroundColor: 'surface.containerLow',
                zIndex: 3,
                minWidth: 130,
              }}
            >
              Plant
            </TableCell>
            {MONTHS.map((m) => (
              <TableCell key={m} colSpan={3} align="center" sx={{ fontWeight: 600, borderBottom: 0 }}>
                {m}
              </TableCell>
            ))}
            <TableCell colSpan={3} align="center" sx={{ fontWeight: 700, borderBottom: 0, backgroundColor: 'surface.containerLow' }}>
              Annual
            </TableCell>
          </TableRow>
          <TableRow>
            {[...MONTHS, 'Annual'].map((m) => (
              ['Cases', 'ECs', 'Hrs'].map((metric) => (
                <TableCell
                  key={`${m}-${metric}`}
                  align="right"
                  sx={{
                    fontSize: '0.65rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                    px: 0.75,
                    minWidth: 52,
                    ...(m === 'Annual' ? { backgroundColor: 'surface.containerLow' } : {}),
                  }}
                >
                  {metric}
                </TableCell>
              ))
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {plantKeys.map((key) => {
            const annual = getPlantAnnual(key)
            return (
              <TableRow key={key} hover>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    position: 'sticky',
                    left: 0,
                    backgroundColor: 'background.paper',
                    zIndex: 1,
                    borderRight: '2px solid',
                    borderRightColor: 'divider',
                  }}
                >
                  {plants[key].name}
                </TableCell>
                {plants[key].data.map((d, mi) => (
                  <MetricCells key={MONTHS[mi]} data={d} />
                ))}
                <MetricCells data={annual} bold />
              </TableRow>
            )
          })}

          <TableRow sx={{ '& td': { borderTop: '2px solid', borderTopColor: 'divider' } }}>
            <TableCell
              sx={{
                fontWeight: 700,
                position: 'sticky',
                left: 0,
                backgroundColor: 'surface.containerLow',
                zIndex: 1,
                borderRight: '2px solid',
                borderRightColor: 'divider',
              }}
            >
              Combined
            </TableCell>
            {MONTHS.map((_, mi) => (
              <MetricCells key={mi} data={getCombined(mi)} bold />
            ))}
            <MetricCells data={getCombinedAnnual()} bold />
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function MetricCells({ data, bold = false }) {
  const sx = {
    fontSize: '0.75rem',
    px: 0.75,
    fontWeight: bold ? 700 : 400,
  }
  return (
    <>
      <TableCell align="right" sx={sx}>{formatNumber(data.cases)}</TableCell>
      <TableCell align="right" sx={sx}>{formatNumber(data.ecs)}</TableCell>
      <TableCell align="right" sx={sx}>{formatNumber(data.hours)}</TableCell>
    </>
  )
}
